import { Input } from "@heroui/react";
import {
  ListBullets,
  Plus,
  Trash,
  X,
} from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { useCallback, useState } from "react";
import { PREDEFINED_ISSUES } from "shared/types/legal-procedure";

interface ProcedureIssuesProps {
  issues: string[];
  onIssuesChanged: (issues: string[]) => void;
}

export const ProcedureIssues = ({
  issues,
  onIssuesChanged,
}: ProcedureIssuesProps) => {
  const [showAddOptions, setShowAddOptions] = useState(false);
  const [customIssue, setCustomIssue] = useState("");

  // Filter out already-added predefined issues
  const availablePredefined = PREDEFINED_ISSUES.filter(
    (pi) => !issues.some((i) => i === pi || i.startsWith(`${pi}:`))
  );

  const handleAddPredefined = useCallback(
    (issue: string) => {
      onIssuesChanged([...issues, issue]);
    },
    [issues, onIssuesChanged]
  );

  const handleAddCustom = useCallback(() => {
    if (!customIssue.trim()) return;
    onIssuesChanged([...issues, customIssue.trim()]);
    setCustomIssue("");
  }, [customIssue, issues, onIssuesChanged]);

  const handleRemove = useCallback(
    (index: number) => {
      onIssuesChanged(issues.filter((_, i) => i !== index));
    },
    [issues, onIssuesChanged]
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <ListBullets size={14} className="text-neutral-secondary-mid" />
          <h5 className="text-xs font-medium text-neutral-secondary-mid">
            Problemen met huurder ({issues.length})
          </h5>
        </div>
        {!showAddOptions && (
          <ButtonNew
            variant="secondaryMoss"
            icon={<Plus size={12} />}
            label="Probleem"
            onClick={() => setShowAddOptions(true)}
          />
        )}
      </div>

      {/* Current issues list */}
      {issues.length > 0 && (
        <div className="flex flex-col gap-1">
          {issues.map((issue, index) => (
            <div
              key={index}
              className="flex items-center gap-2 p-2 rounded bg-red-50 group"
            >
              <span className="text-xs text-red-800 flex-1">{issue}</span>
              <button
                onClick={() => handleRemove(index)}
                className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-red-100 text-red-400 hover:text-red-600 transition-opacity"
              >
                <Trash size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {issues.length === 0 && !showAddOptions && (
        <p className="text-xs text-neutral-secondary-mid italic py-1">
          Nog geen problemen gedefinieerd
        </p>
      )}

      {/* Add options */}
      {showAddOptions && (
        <div className="border rounded-lg p-3 bg-neutral-50 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium">Probleem toevoegen</span>
            <button
              onClick={() => setShowAddOptions(false)}
              className="p-0.5 rounded hover:bg-neutral-200 text-neutral-secondary-mid"
            >
              <X size={14} />
            </button>
          </div>

          {/* Predefined issues as chips */}
          {availablePredefined.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {availablePredefined.map((issue) => (
                <button
                  key={issue}
                  onClick={() => handleAddPredefined(issue)}
                  className="text-[11px] px-2 py-1 rounded-full border border-neutral-200 bg-white hover:bg-moss-primary/10 hover:border-moss-primary/30 text-neutral-secondary-mid hover:text-moss-primary transition-colors"
                >
                  + {issue}
                </button>
              ))}
            </div>
          )}

          {/* Custom issue input */}
          <div className="flex items-center gap-2">
            <Input
              placeholder="Eigen probleem toevoegen..."
              value={customIssue}
              onValueChange={setCustomIssue}
              size="sm"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddCustom();
              }}
            />
            <ButtonNew
              variant="primaryMoss"
              icon={<Plus size={12} />}
              label="Toevoegen"
              onClick={handleAddCustom}
              isDisabled={!customIssue.trim()}
            />
          </div>
        </div>
      )}
    </div>
  );
};
